import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Sparkles, Users, Code2, Settings as SettingsIcon, ChevronDown, LayoutDashboard, History, LayoutTemplate, Images, Lightbulb, DatabaseBackup, UserCog, ShieldCheck, Menu, CalendarClock, Activity } from "lucide-react";
import api, { apiError } from "@/lib/apiClient";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Dashboard from "@/components/admin/Dashboard";
import AIMonitoring from "@/components/admin/AIMonitoring";
import Businesses from "@/components/admin/Businesses";
import ContentGenerator from "@/components/admin/ContentGenerator";
import ContentHistory from "@/components/admin/ContentHistory";
import Templates from "@/components/admin/Templates";
import ContentIdeas from "@/components/admin/ContentIdeas";
import BackupRestore from "@/components/admin/BackupRestore";
import UserManagement from "@/components/admin/UserManagement";
import Leads from "@/components/admin/Leads";
import EmbedScript from "@/components/admin/EmbedScript";
import SettingsPanel from "@/components/admin/Settings";
import MediaLibrary from "@/components/admin/MediaLibrary";
import ScheduledContent from "@/components/admin/ScheduledContent";
import NotificationBell from "@/components/admin/NotificationBell";
import SidebarNavigation from "@/components/admin/SidebarNavigation";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const MENU = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "ai-monitoring", label: "Monitoring AI", icon: Activity },
  { key: "businesses", label: "Kelola Bisnis", icon: Building2 },
  { key: "content", label: "Generate Konten", icon: Sparkles },
  { key: "ideas", label: "Ide Konten", icon: Lightbulb },
  { key: "templates", label: "Template", icon: LayoutTemplate },
  { key: "media", label: "Galeri Media", icon: Images },
  { key: "scheduled", label: "Konten Terjadwal", icon: CalendarClock },
  { key: "history", label: "Riwayat & Jadwal", icon: History },
  { key: "leads", label: "Leads & WhatsApp", icon: Users },
  { key: "users", label: "Kelola User", icon: UserCog },
  { key: "embed", label: "Script Embed", icon: Code2 },
  { key: "backup", label: "Backup & Restore", icon: DatabaseBackup },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

const ROLE_MENUS = {
  super_admin: ["dashboard", "ai-monitoring", "businesses", "content", "ideas", "templates", "media", "scheduled", "history", "leads", "users", "embed", "backup", "settings"],
  business_admin: ["dashboard", "leads", "history", "content", "templates", "ideas", "media", "scheduled"],
  operator: ["dashboard", "content", "history", "scheduled", "media"],
};

const ROLE_LABEL = { super_admin: "Super Admin", business_admin: "Admin Bisnis", operator: "Operator" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [businesses, setBusinesses] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateForContent, setTemplateForContent] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [businessError, setBusinessError] = useState("");
  const [accountError, setAccountError] = useState("");
  const [editKeyBizId, setEditKeyBizId] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    const closeOnDesktop = () => { if (window.innerWidth >= 1024) setMobileNavOpen(false); };
    window.addEventListener("resize", closeOnDesktop);
    return () => window.removeEventListener("resize", closeOnDesktop);
  }, []);
  const selectTab = (key) => { setTab(key); setMobileNavOpen(false); };
  const openBusinessKeyEditor = (businessId) => { setEditKeyBizId(businessId); setTab("businesses"); };

  const useTemplateInGenerator = (tpl) => {
    setTemplateForContent(tpl);
    setTab("content");
  };

  const loadBusinesses = async () => {
    setLoading(true);
    setBusinessError("");
    try {
      const r = await api.get("/businesses");
      setBusinesses(r.data);
      setActiveId((prev) => {
        if (prev && r.data.find((b) => b.id === prev)) return prev;
        return r.data[0]?.id || null;
      });
    } catch (error) {
      setBusinessError(apiError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadAccount = async () => {
    setAccountError("");
    try {
      const r = await api.get("/auth/me");
      const u = r.data.user;
      setCurrentUser(u);
      const allowed = ROLE_MENUS[u.role] || ROLE_MENUS.operator;
      setTab((t) => (allowed.includes(t) ? t : allowed[0]));
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin");
      else setAccountError(apiError(error));
    }
  };

  useEffect(() => {
    loadAccount();
    loadBusinesses();
    // eslint-disable-next-line
  }, []);

  const role = currentUser?.role || "operator";
  const visibleMenu = MENU.filter((m) => (ROLE_MENUS[role] || ROLE_MENUS.operator).includes(m.key));
  const canAccess = (key) => (ROLE_MENUS[role] || ROLE_MENUS.operator).includes(key);

  const activeBusiness = businesses.find((b) => b.id === activeId) || null;

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("currentUser");
    navigate("/admin");
  };

  if (!currentUser) return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-600 p-8">
      {accountError ? (
        <div role="alert" data-testid="account-load-error">
          <p className="text-[#E11D48]">Gagal memuat akun: {accountError}</p>
          <button onClick={loadAccount} className="mt-3 text-[#3B82F6] hover:underline">Coba lagi</button>
        </div>
      ) : "Memuat akun..."}
    </div>
  );

  return (
    <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex">
      {/* Sidebar */}
      <aside data-testid="desktop-sidebar" className="hidden lg:flex w-64 border-r border-[#E2E8F0] bg-[#FFFFFF] flex-col fixed inset-y-0 left-0 h-[100dvh]">
        <SidebarNavigation menu={visibleMenu} tab={tab} onSelect={selectTab} onLogout={logout} />
      </aside>
      <SheetContent side="left" data-testid="mobile-navigation" className="flex h-[100dvh] w-[min(20rem,85vw)] flex-col gap-0 border-[#E2E8F0] bg-[#FFFFFF] p-0 text-slate-900">
        <SheetTitle className="sr-only">Navigasi ContentChatAI</SheetTitle>
        <SheetDescription className="sr-only">Pilih halaman yang ingin dibuka.</SheetDescription>
        <SidebarNavigation mobile menu={visibleMenu} tab={tab} onSelect={selectTab} onLogout={logout} />
      </SheetContent>

      {/* Main */}
      <div className="min-w-0 w-full flex-1 lg:ml-64 flex flex-col">
        <header className="min-h-16 border-b border-[#E2E8F0] flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8 sticky top-0 bg-[#F8FAFC]/95 backdrop-blur-xl z-20">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <SheetTrigger asChild><button aria-label="Buka menu navigasi" data-testid="mobile-menu-button" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E2E8F0] bg-[#FFFFFF] lg:hidden"><Menu size={20} /></button></SheetTrigger>
            <h1 className="min-w-0 font-heading font-bold text-base sm:text-lg">{MENU.find((m) => m.key === tab)?.label}</h1>
          </div>
          <div className="flex w-full min-w-0 items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-3">
            {currentUser && (
              <div className="flex min-w-0 flex-1 sm:flex-none items-center gap-2 bg-[#FFFFFF] border border-[#E2E8F0] rounded-xl px-2 sm:px-3 py-1.5 text-xs" data-testid="current-user-badge" title={`${currentUser.full_name || currentUser.username} · ${ROLE_LABEL[currentUser.role]}`}>
                <ShieldCheck size={14} className="shrink-0 text-[#059669]" />
                <div className="min-w-0"><span className="block max-w-28 truncate text-slate-700 font-medium">{currentUser.full_name || currentUser.username}</span><span className="block truncate text-[10px] text-slate-500 md:hidden">{ROLE_LABEL[currentUser.role]}</span></div>
                <span className="hidden shrink-0 text-slate-500 md:inline">· {ROLE_LABEL[currentUser.role]}</span>
              </div>
            )}
            {role !== "operator" && <NotificationBell />}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="business-selector-dropdown"
                className="flex min-h-11 min-w-0 max-w-[48%] sm:max-w-52 items-center gap-2 bg-[#FFFFFF] border border-[#E2E8F0] rounded-xl px-2 sm:px-4 py-2 text-sm hover:border-[#3B82F6] transition-colors"
              >
                <Building2 size={16} className="shrink-0 text-[#2563EB]" />
                <span className="min-w-0 max-w-[160px] truncate">{activeBusiness?.name || "Belum ada bisnis"}</span>
                <ChevronDown size={14} className="shrink-0 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
              {businesses.length === 0 && <DropdownMenuItem disabled>Belum ada bisnis</DropdownMenuItem>}
              {businesses.map((b) => (
                <DropdownMenuItem
                  key={b.id}
                  onClick={() => setActiveId(b.id)}
                  data-testid={`business-option-${b.id}`}
                  className="max-w-[calc(100vw-2rem)] cursor-pointer whitespace-normal break-words focus:bg-[#F1F5F9]"
                >
                  {b.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main data-testid="admin-content" className="admin-content min-w-0 p-4 sm:p-6 lg:p-8 flex-1">
          {businessError && (
            <div role="alert" data-testid="business-load-error" className="mb-6 rounded-xl border border-[#F43F5E]/30 bg-[#F43F5E]/10 p-4 text-sm">
              <p className="font-semibold text-[#E11D48]">Gagal memuat bisnis</p>
              <p className="mt-1 text-slate-700">{businessError}</p>
              <button onClick={loadBusinesses} className="mt-3 text-[#3B82F6] hover:underline">Coba lagi</button>
            </div>
          )}
          {tab === "dashboard" && canAccess("dashboard") && <Dashboard key={businesses.length} businesses={businesses} role={role} onEditBusinessKey={role === "super_admin" ? openBusinessKeyEditor : undefined} />}
          {tab === "ai-monitoring" && canAccess("ai-monitoring") && <AIMonitoring businesses={businesses} role={role} />}
          {tab === "businesses" && canAccess("businesses") && (
            <Businesses businesses={businesses} loading={loading} reload={loadBusinesses} setActiveId={setActiveId} editKeyBizId={editKeyBizId} onEditKeyHandled={() => setEditKeyBizId(null)} />
          )}
          {tab === "content" && <ContentGenerator role={role} businesses={businesses} activeBusiness={activeBusiness} initialTemplate={templateForContent} />}
          {tab === "ideas" && <ContentIdeas role={role} activeBusiness={activeBusiness} onUseIdea={useTemplateInGenerator} />}
          {tab === "templates" && canAccess("templates") && <Templates currentUser={currentUser} role={role} onUseTemplate={useTemplateInGenerator} />}
          {tab === "media" && canAccess("media") && <MediaLibrary />}
          {tab === "scheduled" && canAccess("scheduled") && <ScheduledContent businesses={businesses} role={role} />}
          {tab === "history" && <ContentHistory businesses={businesses} role={role} />}
          {tab === "leads" && canAccess("leads") && <Leads activeBusiness={activeBusiness} businesses={businesses} role={role} />}
          {tab === "users" && canAccess("users") && <UserManagement businesses={businesses} currentUser={currentUser} />}
          {tab === "embed" && canAccess("embed") && <EmbedScript activeBusiness={activeBusiness} userId={currentUser.id} reload={loadBusinesses} />}
          {tab === "backup" && canAccess("backup") && <BackupRestore />}
          {tab === "settings" && canAccess("settings") && <SettingsPanel activeBusiness={activeBusiness} reload={loadBusinesses} role={role} onUserChanged={setCurrentUser} />}
        </main>
      </div>
    </div>
    </Sheet>
  );
}
